import React from 'react'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import gql from 'graphql-tag'
import { withApollo } from '@apollo/react-hoc'
import { Mutation } from '@apollo/react-components'
import cookies from 'js-cookie'
import { Alert, Button, space } from '@ticketswap/solar'
import { Facebook } from '@ticketswap/comets'
import Login from 'react-facebook/dist/Login'
import {
  AUTHENTICATION_STARTED,
  AUTHENTICATION_COMPLETED,
} from '../../lib/tracking-events'
import { track, setTrackingIdentity } from '../../utils'
import getViewer from '../../lib/get-viewer'
import { GENERATE_AUTH_TOKEN } from './generateAuthToken'
import { useViewer } from '../ViewerContext'

const { CLIENT_ID, TOKEN_COOKIE_NAME } = getConfig().publicRuntimeConfig

const AUTHENTICATE_USER_WITH_FACEBOOK = gql`
  mutation authenticateUserWithFacebook(
    $input: AuthenticateFacebookUserInput!
  ) {
    facebookAuth: authenticateUserWithFacebook(input: $input) {
      apiAccessToken {
        tokenString
        expiresAt
      }
      isNew
    }
  }
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${space[8]};
`

function FacebookAuthButton(props) {
  const [errorMessage, setErrorMessage] = React.useState(null)
  const [loadingFacebook, setLoadingFacebook] = React.useState(false)
  const { setViewer } = useViewer()

  const handleCompleted = async (facebookData, generate, authenticate) => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: CLIENT_ID,
        },
      },
    })

    const {
      data: { facebookAuth },
    } = await authenticate({
      variables: {
        input: {
          token: authenticateClientForToken.token,
          facebookAccessToken: facebookData.tokenDetail.accessToken,
        },
      },
    })
    const { tokenString } = facebookAuth.apiAccessToken

    cookies.set(TOKEN_COOKIE_NAME, tokenString, {
      expires: 30,
      secure: true,
    })

    const user = await getViewer(props.client)
    setTrackingIdentity(user)

    if (facebookAuth.isNew) {
      // Early return so we don’t call `resetStore()` in this case (Will be
      // called in `SignupSocial.js` instead)
      return props.onSignup({
        firstName: facebookData.profile.first_name,
        lastName: facebookData.profile.last_name,
      })
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      isNew: facebookAuth.isNew,
    })

    await props.client.resetStore()
    setViewer(user)
    props.onLogin()
  }

  const handleError = error => {
    setLoadingFacebook(false)
    let message = ''
    if (typeof error === 'string') {
      message = error
    } else if (error.message) {
      message = error.message
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      error: message,
    })

    setErrorMessage(message)
  }

  return (
    <Mutation mutation={GENERATE_AUTH_TOKEN}>
      {generate => (
        <Mutation mutation={AUTHENTICATE_USER_WITH_FACEBOOK}>
          {authenticate => (
            <Login
              scope="email,user_friends,user_location,user_birthday"
              onCompleted={facebookData => {
                handleCompleted(facebookData, generate, authenticate)
              }}
              onError={handleError}
            >
              {({ handleClick, loading }) => (
                <>
                  {errorMessage && (
                    <StyledAlert variant="error">{errorMessage}</StyledAlert>
                  )}
                  <Button
                    loading={loadingFacebook}
                    disabled={loading}
                    variant="facebook"
                    width="full"
                    leftAdornment={<Facebook size={24} />}
                    onClick={event => {
                      setLoadingFacebook(true)
                      track(AUTHENTICATION_STARTED, {
                        method: 'facebook',
                      })
                      handleClick(event)
                    }}
                  >
                    {props.children}
                  </Button>
                </>
              )}
            </Login>
          )}
        </Mutation>
      )}
    </Mutation>
  )
}

export default withApollo(FacebookAuthButton)
